/** Platform-aware file export utilities. Web uses Blob+anchor, native uses expo-file-system + expo-sharing. */
import { Platform, Alert } from 'react-native';

/** Share a cached file via the system share sheet. No special permissions needed. */
async function saveFileNative(uri: string, _filename: string, mimeType: string): Promise<void> {
  const { shareAsync } = await import('expo-sharing');
  try {
    await shareAsync(uri, { mimeType });
  } catch (err: any) {
    Alert.alert('Export Error', err.message || 'Failed to export file');
  }
}

/**
 * Export CSV by fetching from a URL with auth headers.
 * Web: fetch -> Blob -> anchor download. Native: File.downloadFileAsync -> share.
 */
export async function exportCsvFromUrl(url: string, headers: Record<string, string>, filename: string): Promise<void> {
  if (Platform.OS === 'web') {
    const res = await fetch(url, { headers, credentials: 'include' });
    const csv = await res.text();
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } else {
    const { File, Paths } = await import('expo-file-system');
    // Use unique filename to avoid Android share sheet cache issues
    const uniqueName = `${Date.now()}-${filename}`;
    const dest = new File(Paths.cache, uniqueName);
    const downloaded = await File.downloadFileAsync(url, dest, { headers });
    await saveFileNative(downloaded.uri, filename, 'text/csv');
  }
}

/**
 * Export PDF by fetching from a URL with auth headers.
 * Web: fetch -> Blob -> anchor download. Native: File.downloadFileAsync -> share.
 */
export async function exportPdfFromUrl(url: string, headers: Record<string, string>, filename: string): Promise<void> {
  if (Platform.OS === 'web') {
    const res = await fetch(url, { headers, credentials: 'include' });
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } else {
    const { File, Paths } = await import('expo-file-system');
    const uniqueName = `${Date.now()}-${filename}`;
    const dest = new File(Paths.cache, uniqueName);
    const downloaded = await File.downloadFileAsync(url, dest, { headers });
    await saveFileNative(downloaded.uri, filename, 'application/pdf');
  }
}

/**
 * Export CSV from an in-memory string.
 * Web: Blob -> anchor download. Native: File.write -> share.
 */
export async function exportCsvFromString(csvContent: string, filename: string): Promise<void> {
  if (Platform.OS === 'web') {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  } else {
    const { File, Paths } = await import('expo-file-system');
    const uniqueName = `${Date.now()}-${filename}`;
    const file = new File(Paths.cache, uniqueName);
    await file.write(csvContent);
    await saveFileNative(file.uri, filename, 'text/csv');
  }
}

/**
 * Download a file (e.g. QR SVG) and trigger save/share.
 * Handles both HTTP URLs and data URIs (base64-encoded).
 * Web: anchor download. Native: write to cache -> share sheet.
 */
export async function downloadFile(url: string, filename: string, headers?: Record<string, string>): Promise<void> {
  if (Platform.OS === 'web') {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  } else {
    const { File, Paths } = await import('expo-file-system');
    const uniqueName = `${Date.now()}-${filename}`;
    const dest = new File(Paths.cache, uniqueName);

    if (url.startsWith('data:')) {
      // Data URI: decode and write directly to cache
      const base64Match = url.match(/^data:[^;]+;base64,(.+)$/);
      if (base64Match) {
        await dest.write(base64Match[1], { encoding: 'base64' });
      } else {
        // Plain data URI (non-base64)
        const content = decodeURIComponent(url.split(',')[1] || '');
        await dest.write(content);
      }
    } else {
      // HTTP URL: download to cache
      await File.downloadFileAsync(url, dest, headers ? { headers } : undefined);
    }

    const mimeType = filename.endsWith('.svg') ? 'image/svg+xml' : 'application/octet-stream';
    await saveFileNative(dest.uri, filename, mimeType);
  }
}
